import React, { useState, useEffect } from "react";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import { getFormattedDateTime, useToast } from "../../services/utils";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import ButtonComponent from "../../components/Button.Component";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import {
  deleteUserAuthTag,
  reactivateAuthTag,
} from "../../services/authTagService";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import {
  IconEditBase,
  IconDeleteBlueSvg,
  IconEyeBase,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconReactivateSvg,
} from "../../common/utils/icons";
import usePaginatedFetchAuthTagsList from "../../hooks/usePaginatedFetchAuthTagsList";
import useDebounce from "../../hooks/useDebounce";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { enableRowGroupByColumnProps } from "../../components/DataGridProps/DataGridProps";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import Loader from "../../components/Molecules/Loader/Loader";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { useAuth } from "../../contexts/AuthContext";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { getConfig } from "../../config/runtimeConfig";

// Centralized Filter Configuration for Tags List
const TAGS_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    tag_name: "",
    status_filter: "Active",
  },

  // Fields to persist in URL
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: [],
    STRING_FIELDS: ["tag_name", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const AuthTags: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { t } = useAppTranslation();
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const { rowsPerPage } = useRowsPerPage();

  // Permission checks for auth tag actions
  const canViewAuthTag = hasPermission("auth_tag", "view");
  const canAddAuthTag = hasPermission("auth_tag", "add");
  const canEditAuthTag = hasPermission("auth_tag", "edit");
  const canDeleteAuthTag = hasPermission("auth_tag", "delete");

  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // SearchFilterBar state - initialize from URL parameters
  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = {
      ...TAGS_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Restore filters from URL parameters
    TAGS_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        initialState[field] = urlValue;
      }
    });

    return initialState;
  });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [authTagToDelete, setAuthTagToDelete] = useState<any>(null);

  // Extract filters from searchFilterData
  const effectiveStatusFilter = searchFilterData?.status_filter || "";
  const effectiveTagName = searchFilterData?.tag_name || "";

  // Debounce tag_name with 400ms delay
  const debouncedTagName = useDebounce(effectiveTagName);

  // Determine if we should include tag_name in API call
  const effectiveTagNameSearch =
    debouncedTagName.trim().length >= 3 ? debouncedTagName : "";

  // Convert status_filter to isActive parameter
  let isActiveParam = "true"; // Default to active
  if (effectiveStatusFilter === "Inactive") {
    isActiveParam = "false";
  } else if (effectiveStatusFilter === "All") {
    isActiveParam = "all";
  }

  const [authTagsData, refetchAuthTags] = usePaginatedFetchAuthTagsList({
    setIsLoading,
    page,
    pSize,
    searchTerm: effectiveTagNameSearch,
    isActive: isActiveParam,
  });

  // Sync filters to URL parameters (state → URL)
  useEffect(() => {
    const newParams = new URLSearchParams();

    TAGS_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = searchFilterData[field];
      if (value !== null && value !== undefined && value !== "") {
        // Don't add default values to URL to keep it clean
        if (field === "status_filter" && value === "Active") {
          return; // Skip default status filter
        }
        newParams.set(field, String(value));
      }
    });

    // Only update URL if params have changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → state for back/forward navigation)
  useEffect(() => {
    const urlFilterState: any = {
      ...TAGS_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    TAGS_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        urlFilterState[field] = urlValue;
      }
    });

    // Check if URL filter state differs from current filter state
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const urlValue = urlFilterState[key];
      const currentValue = searchFilterData[key];

      // Handle null/undefined/empty string comparisons
      const normalizedUrlValue =
        urlValue === null || urlValue === undefined || urlValue === ""
          ? ""
          : urlValue;
      const normalizedCurrentValue =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
          ? ""
          : currentValue;

      return normalizedUrlValue !== normalizedCurrentValue;
    });

    // Only update state if there are actual changes to prevent infinite loops
    if (hasFilterChanges) {
      setSearchFilterData(urlFilterState);
    }
  }, [searchParams]); // Only depend on searchParams to detect URL changes

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [effectiveTagNameSearch, effectiveStatusFilter]);

  // Handle delete tag
  const handleDeleteAuthTag = (authTag: any) => {
    setAuthTagToDelete(authTag);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!authTagToDelete) return;

    try {
      setIsLoading(true);
      await deleteUserAuthTag(authTagToDelete.id);
      showToast(t("authTags.toast.deleted"), "success");
      setDeleteConfirmOpen(false);
      setAuthTagToDelete(null);
      // Refetch the tags list to update the UI
      refetchAuthTags();
    } catch (error: any) {
      console.error("Error deleting tag:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reactivate tag
  const handleReactivate = async (authTag: any) => {
    try {
      setIsLoading(true);
      await reactivateAuthTag(authTag.id);
      showToast(t("authTags.toast.reactivated"), "success");
      // Refetch the tags list to update the UI
      refetchAuthTags();
    } catch (error: any) {
      console.error("Error reactivating tag:", error);
      showToast(
        error?.response?.data?.detail || t("authTags.toast.failedReactivate"),
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: () => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "id",
      headerName: t("authTags.list.columns.id"),
      width: 100,
      sortable: true,
    },
    {
      field: "name",
      headerName: t("authTags.list.columns.name"),
      width: 100,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => (
        <LimitChractersWithTooltip value={params.value || ""} />
      ),
    },
    {
      field: "description",
      headerName: t("authTags.list.columns.description"),
      width: 200,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => (
        <LimitChractersWithTooltip
          value={params.value || t("authTags.list.noDescription")}
        />
      ),
    },
    {
      field: "modified_by_user",
      headerName: t("authTags.list.columns.modifiedBy"),
      width: 150,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => (
        <LimitChractersWithTooltip value={params?.value?.username} />
      ),
    },
    {
      field: "updated_on",
      headerName: t("authTags.list.columns.updatedOn"),
      width: 150,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => {
        return getFormattedDateTime(params.value);
      },
    },
    {
      field: "is_active",
      headerName: t("authTags.list.columns.status"),
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        return params.value ? (
          <Tooltip
            title={t("authTags.list.tooltip.active")}
            placement="top"
            arrow
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheckCircleIconGreenSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip
            title={t("authTags.list.tooltip.inactive")}
            placement="top"
            arrow
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCrossCircleIconSvg width={21} height={21} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: t("authTags.list.columns.actions"),
      align: "center",
      headerAlign: "center",
      width: 160,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        const isTagActive = params.row.is_active === true;

        return (
          <>
            {/* For active records, show all action buttons */}
            {isTagActive && (
              <>
                {canViewAuthTag && (
                  <Tooltip
                    title={t("authTags.list.tooltip.view")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => navigate(`/tags/view/${params.row.id}`)}
                    >
                      <IconEyeBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canEditAuthTag && (
                  <Tooltip
                    title={t("authTags.list.tooltip.edit")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => navigate(`/tags/edit/${params.row.id}`)}
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canDeleteAuthTag && (
                  <Tooltip
                    title={t("authTags.list.tooltip.delete")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => handleDeleteAuthTag(params.row)}
                    >
                      <IconDeleteBlueSvg />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}

            {/* For inactive records, show ONLY Reactivate button */}
            {!isTagActive && canEditAuthTag && (
              <Tooltip
                title={t("authTags.list.tooltip.reactivate")}
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => handleReactivate(params.row)}
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Box>
      <Loader isLoading={isLoading} />

      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle={t("authTags.dialog.deleteTitle")}
        dailogDescription={t("authTags.dialog.deleteDescription", {
          name: authTagToDelete?.name,
        })}
      />

      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              searchFilterData={searchFilterData}
              setSearchFilterData={setSearchFilterData}
              requiredFields={["tag_name", "status_filter"]}
              primaryFields={["tag_name", "status_filter"]}
              fieldRequirements={{
                defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
              }}
            />
          </Grid>
          {canAddAuthTag && (
            <Grid
              item
              xs="auto"
              sx={{ display: "flex", alignItems: "flex-start" }}
            >
              <ButtonComponent
                handelClickEvent={() => navigate("/tags/add")}
                className="btn-orange"
              >
                <>
                  <AddSharpIcon sx={{ marginRight: "4px" }} />
                  {t("authTags.list.title")}
                </>
              </ButtonComponent>
            </Grid>
          )}
        </Grid>
      </Box>

      <FlexBetween>
        <DataTable
          dataColumns={columns || []}
          dataRows={authTagsData?.items || []}
          loading={isLoading}
          dataListTitle={t("authTags.list.dataListTitle")}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={authTagsData?.total || 0}
          rowGroupByColumnProps={enableRowGroupByColumnProps}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          fitViewport
          singlePageMaxHeightDiff={358}
        />
      </FlexBetween>
    </Box>
  );
};

export default AuthTags;
